/**
 * useFrameData - 帧数据二分查找 Hook
 *
 * Provides O(log n) binary-search frame lookup for the 100 Hz SkiTickData
 * array, plus a memoized React hook for use in components.
 *
 * 为 100 Hz 的 SkiTickData 数组提供 O(log n) 二分查找，并提供
 * 供组件使用的 memoized React Hook。
 *
 * The frames array is assumed to be sorted ascending by `timestamp_ms`.
 * 帧数组按 `timestamp_ms` 升序排列。
 */

import { useMemo } from 'react';
import type { SkiTickData } from '../types/ski-data';

/**
 * Binary search for the frame whose `timestamp_ms` is the greatest value
 * less than or equal to `timestampMs`.
 *
 * 在 frames 中二分查找 `timestamp_ms` 小于等于 `timestampMs` 的最大帧索引。
 *
 * @param frames       - ascending-by-timestamp frames / 按时间戳升序的帧数组
 * @param timestampMs  - target timestamp in ms / 目标时间戳（毫秒）
 * @returns index of the closest frame at or before the target;
 *          returns 0 for empty arrays or timestamps before the first frame.
 *          返回小于等于目标时间戳的最大帧索引；空数组或目标早于首帧时返回 0。
 */
export function findFrameIndex(
  frames: SkiTickData[],
  timestampMs: number
): number {
  if (frames.length === 0) return 0;

  let lo = 0;
  let hi = frames.length - 1;

  // Upper-bound binary search: find the rightmost frame with timestamp_ms <= target.
  // 上界二分查找：寻找 timestamp_ms <= target 的最右帧。
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (frames[mid].timestamp_ms <= timestampMs) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  return lo;
}

/**
 * Look up the frame closest to (at or before) `timestampMs`.
 *
 * 查找小于等于 `timestampMs` 的最近帧。
 *
 * @param frames       - ascending-by-timestamp frames / 按时间戳升序的帧数组
 * @param timestampMs  - target timestamp in ms / 目标时间戳（毫秒）
 * @returns the matching frame (frames[0] for empty arrays)
 */
export function findFrame(
  frames: SkiTickData[],
  timestampMs: number
): SkiTickData {
  const idx = findFrameIndex(frames, timestampMs);
  return frames[idx];
}

/**
 * Memoized frame lookup hook. Recomputes only when `frames` or `timestampMs`
 * change, returning the matched frame and its index.
 *
 * Memoized 帧查找 Hook。仅在 `frames` 或 `timestampMs` 变化时重新计算，
 * 返回匹配的帧及其索引。
 *
 * @param frames       - ascending-by-timestamp frames / 按时间戳升序的帧数组
 * @param timestampMs  - target timestamp in ms / 目标时间戳（毫秒）
 * @returns `{ frame, index }` — frame is `frames[index]`
 */
export function useFrameData(
  frames: SkiTickData[],
  timestampMs: number
): { frame: SkiTickData; index: number } {
  const index = useMemo(
    () => findFrameIndex(frames, timestampMs),
    [frames, timestampMs]
  );

  // `frame` is derived from `frames` and `index`; useMemo avoids re-slicing
  // on every render when frames is a stable reference.
  const frame = useMemo(() => frames[index], [frames, index]);

  return { frame, index };
}
